import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, ExternalLink, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { getPilot, listPilotLeads } from "@/lib/api";

const urgencyColor = {
  low: "#7dd3a0",
  medium: "#e5c454",
  high: "#e56d5a",
};

export default function AdminPilotDetail() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    Promise.all([getPilot(slug), listPilotLeads(slug)])
      .then(([b, l]) => {
        if (!ignore) {
          setBusiness(b);
          setLeads(l);
        }
      })
      .catch(() => toast.error("Failed to load pilot details."))
      .finally(() => !ignore && setLoading(false));
    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen w-full bg-[#0a0a0b] text-white/80 flex items-center justify-center">
        <div className="flex items-center gap-3 font-mono-plex text-[11px] uppercase tracking-[0.3em] text-white/40">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </main>
    );
  }

  if (!business) return null;

  return (
    <main
      className="min-h-screen w-full bg-[#0a0a0b] text-white/90"
      data-testid="admin-pilot-detail-page"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 md:px-16 py-14 md:py-20">
        {/* Header */}
        <div className="mb-14">
          <Link
            to="/admin/pilots"
            className="inline-flex items-center gap-2 font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/45 hover:text-white/90 transition-colors mb-8"
            data-testid="admin-detail-back"
          >
            <ArrowLeft className="h-3 w-3" /> All pilots
          </Link>

          <div className="flex items-start justify-between flex-wrap gap-6">
            <div>
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-white/40 mb-4">
                <span>/{business.slug}</span>
                <span className="mx-2 text-white/20">·</span>
                <span>{business.industry}</span>
              </div>
              <h1 className="font-serif-display text-white text-4xl md:text-5xl leading-tight">
                {business.name}
              </h1>
              {business.city && (
                <p className="font-mono-plex text-sm text-white/45 mt-3">{business.city}</p>
              )}
            </div>
            <Link
              to={`/${business.slug}`}
              target="_blank"
              rel="noreferrer"
              className="h-11 px-5 border border-white/20 text-white/80 hover:bg-white hover:text-black font-mono-plex text-[11px] uppercase tracking-[0.24em] inline-flex items-center gap-2 transition-colors"
              data-testid="admin-detail-view-live"
            >
              View live page <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Trial status */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8">
            <Stat label="Trial days left" value={business.trial_days_remaining} />
            <Stat label="Trial length" value={`${business.trial_days} days`} />
            <Stat label="Total leads" value={leads.length} />
            <Stat label="Status" value={business.active ? "Live" : "Paused"} />
          </div>
        </div>

        {/* Leads */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif-display text-white text-2xl">Service Writer leads</h2>
            <span className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/40">
              {leads.length} total
            </span>
          </div>

          {leads.length === 0 ? (
            <div
              className="border border-dashed border-white/15 p-12 text-center"
              data-testid="admin-detail-empty-leads"
            >
              <p className="font-serif-display text-white/80 text-xl mb-3">No leads yet.</p>
              <p className="font-mono-plex text-[12px] text-white/45">
                When customers submit through the Service Writer form, they'll show up here.
              </p>
            </div>
          ) : (
            <ul className="space-y-4" data-testid="admin-detail-leads-list">
              {leads.map((lead) => {
                const w = lead.ai_writeup;
                const uColor = w ? urgencyColor[w.urgency] || "#c9a24a" : "#5c5c60";
                return (
                  <li
                    key={lead.id}
                    className="border border-white/10 bg-white/[0.02] p-6"
                    data-testid={`admin-lead-${lead.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ background: uColor }}
                        />
                        <span className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/50">
                          {w ? `${w.urgency} urgency` : "AI write-up pending"}
                        </span>
                      </div>
                      <span className="font-mono-plex text-[10px] text-white/35">
                        {new Date(lead.created_at).toLocaleString()}
                      </span>
                    </div>

                    <div className="mt-4 flex items-start gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-white text-xl leading-snug">
                          {w?.issue_summary || lead.raw_description}
                        </div>
                        {w?.recommended_service && (
                          <div className="mt-3 font-mono-plex text-[12px] text-white/60">
                            → {w.recommended_service}
                          </div>
                        )}
                      </div>
                      <div className="font-mono-plex text-[11px] text-white/60 space-y-1 shrink-0">
                        <div className="text-white/85">{lead.customer_name}</div>
                        <a
                          href={`mailto:${lead.customer_email}`}
                          className="flex items-center gap-2 text-white/50 hover:text-white/90"
                        >
                          <Mail className="h-3 w-3" /> {lead.customer_email}
                        </a>
                        {lead.customer_phone && (
                          <a
                            href={`tel:${lead.customer_phone}`}
                            className="flex items-center gap-2 text-white/50 hover:text-white/90"
                          >
                            <Phone className="h-3 w-3" /> {lead.customer_phone}
                          </a>
                        )}
                      </div>
                    </div>

                    {w?.prep_questions?.length ? (
                      <div className="mt-5 pt-5 border-t border-white/10">
                        <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35 mb-2">
                          Prep questions
                        </div>
                        <ul className="space-y-1">
                          {w.prep_questions.map((q, i) => (
                            <li
                              key={i}
                              className="font-mono-plex text-[12px] text-white/70 flex items-baseline gap-2"
                            >
                              <span className="text-white/25">{String(i + 1).padStart(2, "0")}</span>
                              <span>{q}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}

                    {w?.internal_notes && (
                      <div className="mt-5 pt-5 border-t border-white/10">
                        <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35 mb-2">
                          Internal notes
                        </div>
                        <p className="font-mono-plex text-[12px] text-white/60 leading-relaxed">
                          {w.internal_notes}
                        </p>
                      </div>
                    )}

                    <details className="mt-5 pt-5 border-t border-white/10">
                      <summary className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35 cursor-pointer hover:text-white/70">
                        Customer's raw words
                      </summary>
                      <p className="mt-3 font-mono-plex text-[12px] text-white/55 leading-relaxed whitespace-pre-wrap">
                        {lead.raw_description}
                      </p>
                    </details>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono-plex text-[10px] uppercase tracking-[0.28em] text-white/35 mb-2">
      {label}
    </div>
    <div className="font-serif-display text-white text-2xl md:text-3xl">{value}</div>
  </div>
);
